import { useEffect, useRef } from 'react';
import { signOut, useSession } from 'next-auth/react';
import router from 'next/router';

import { useUserContext } from '../../contexts/UserContext';

import { getCurrentTimeInSeconds } from '../../utils/Time';
import { userLogout } from '../../helpers/Logout';
import { isProtectedRoute } from '../../constants/protectedRoutes';
import { ENVIRONMENT_NAME } from '../../utils';
import { PAGE } from '../../helpers/pages';
import { randomStringGenerator } from '../../helpers/randomStringGenerator';
import { generateDDRecommendedUserCookie } from '../../utils/ddRecommendedUserCookie';
import { NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE } from '../../helpers/constants';
import { logger } from '@dist-marketplace-frontend/services';

interface RefreshTokenHandlerProps {
  setRefetchInterval: (interval: number) => void;
}

const RefreshTokenHandler = (props: RefreshTokenHandlerProps) => {
  const { setRefetchInterval } = props;
  const { data: session, status } = useSession();

  const {
    dispatchCookieBasedUser,
    cookieBasedUser,
    consentOptions: { functionalConsent },
  } = useUserContext();

  // Use a ref to remove username from the effect's dependency array
  // as this doesn't make sense to re-trigger the effect when it changes
  // When using React 19 we can replace this with a useEffectEvent
  const usernameRef = useRef(cookieBasedUser?.name || '');
  usernameRef.current = cookieBasedUser?.name || '';

  useEffect(() => {
    if (status !== 'authenticated') {
      return;
    }

    if (tokenHasAnError()) {
      handleSignOut();
    } else {
      updateRefetchInterval();
    }

    function updateRefetchInterval() {
      const currentTimeInSeconds = getCurrentTimeInSeconds();

      if (typeof session?.expiresAt === 'number') {
        const timeRemaining = session.expiresAt - currentTimeInSeconds;
        const refetchInterval = timeRemaining > 0 ? timeRemaining : 0;
        setRefetchInterval(refetchInterval);
      }
    }

    async function handleSignOut() {
      try {
        await Promise.all([
          handleLegacyLogout(),
          signOut(
            isProtectedRoute(location.pathname)
              ? { callbackUrl: '/' }
              : { redirect: false },
          ),
        ]);
      } catch (error) {
        logger.error({
          message:
            'An error occurred while trying to force the user to log out',
          error,
        });
      }

      async function handleLegacyLogout() {
        if (functionalConsent) {
          document.cookie = generateDDRecommendedUserCookie(
            randomStringGenerator(),
          );
        }
        if (ENVIRONMENT_NAME === 'dev') {
          router.push(`/${PAGE.LOCAL_DEV_SIGNOUT}`);
        } else {
          await userLogout(dispatchCookieBasedUser, usernameRef.current);
        }
      }
    }

    function tokenHasAnError() {
      return session?.error === NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE;
    }
  }, [
    dispatchCookieBasedUser,
    setRefetchInterval,
    session?.error,
    session?.expiresAt,
    status,
    functionalConsent,
  ]);

  return null;
};

export default RefreshTokenHandler;
